import React, { useEffect, useState } from "react";
import { subscriptionAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import { formatDate } from "../utils/dates";
import { formatMoney } from "../pos/utils/money";
import { whatsappLink } from "../utils/support";
import { openDocument } from "../utils/documents";
import OpenAnotherShop, { usePackRoom } from "../components/OpenAnotherShop";

/**
 * The owner's subscription: what they are on, what they pay, when it is due,
 * how to pay, and every payment so far with its receipt. Read-only: payments
 * are recorded by the operator, which is what makes the receipt trustworthy.
 */
const Subscription = () => {
  const { t, lang } = useI18n();
  const { shop } = useMembership();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const room = usePackRoom();

  useEffect(() => {
    subscriptionAPI
      .get()
      .then((response) => setData(response.data))
      .catch(() => setError(t("subscription.page.loadFailed")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewDocument = async (paymentId) => {
    try {
      await openDocument(async () => (await subscriptionAPI.document(paymentId)).data);
    } catch (err) {
      setError(t(err.message === "popup-blocked" ? "subscription.page.popupBlocked" : "subscription.page.openFailed"));
    }
  };

  if (error && !data) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-gray-500">…</p>;

  const money = (value) => formatMoney(value, data.currency);
  const instructions = data.how_to_pay.instructions?.[lang] || data.how_to_pay.instructions?.en || "";
  const methods = data.how_to_pay.methods.map((m) => t(`subscription.page.method.${m}`)).join(", ");
  const whatsapp = whatsappLink(
    data.how_to_pay.whatsapp_number,
    t("subscription.page.whatsappMessage", { shop: shop?.name, slug: shop?.slug })
  );

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t("subscription.page.title")}</h1>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {data.pack && (
        <section className="rounded-xl border border-primary-200 bg-primary-50 p-5 text-sm text-primary-900">
          <h2 className="font-semibold">{t("shops.pack.title", { count: data.pack.shops.length })}</h2>
          <p className="mt-1">
            {data.pack.this_shop_pays
              ? t("shops.pack.thisPays", { shops: data.pack.shops.map((entry) => entry.name).join(", ") })
              : t("shops.pack.paidBy", {
                  name: data.pack.paid_by.name,
                  shops: data.pack.shops.map((entry) => entry.name).join(", "),
                })}
          </p>
        </section>
      )}

      {room?.can_add && (
        <section className="bg-white rounded-xl shadow p-5 space-y-3">
          <p className="text-sm text-gray-700">
            {t("shops.open.room", { count: room.shop_limit - room.pack_size, limit: room.shop_limit })}
          </p>
          <OpenAnotherShop room={room} />
        </section>
      )}

      <section className="bg-white rounded-xl shadow p-5">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">{t("subscription.page.plan")}</dt>
            <dd className="font-semibold text-gray-900">{data.plan_name || t("subscription.page.noPlan")}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("subscription.page.price")}</dt>
            <dd className="font-semibold text-gray-900">
              {data.price == null
                ? "—"
                : t(data.billing_cycle === "yearly" ? "subscription.page.perYearly" : "subscription.page.perMonthly", {
                    amount: money(data.price),
                  })}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("subscription.page.status")}</dt>
            <dd
              className={`font-semibold ${
                ["past_due", "suspended"].includes(data.status) ? "text-red-700" : "text-gray-900"
              }`}
            >
              {t(`subscription.page.statuses.${data.status}`)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("subscription.page.nextDue")}</dt>
            <dd className="font-semibold text-gray-900">
              {data.next_due_on ? formatDate(data.next_due_on) : t("subscription.page.noDate")}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-gray-500 mt-4">{t("subscription.page.grace", { days: data.grace_days })}</p>
      </section>

      <section className="bg-white rounded-xl shadow p-5 space-y-3">
        <h2 className="font-semibold text-gray-800">{t("subscription.page.howToPay")}</h2>
        <p className="text-sm text-gray-700 whitespace-pre-line">
          {instructions || t("subscription.page.noInstructions")}
        </p>
        {methods && <p className="text-sm text-gray-500">{t("subscription.page.methods", { list: methods })}</p>}
        {whatsapp && (
          <a
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2.5 rounded-lg"
          >
            💬 {t("subscription.page.whatsapp")}
          </a>
        )}
      </section>

      <section className="bg-white rounded-xl shadow p-5">
        <h2 className="font-semibold text-gray-800 mb-3">{t("subscription.page.history")}</h2>
        {data.payments.length === 0 ? (
          <p className="text-sm text-gray-500">{t("subscription.page.none")}</p>
        ) : (
          <ul className="divide-y">
            {data.payments.map((payment) => (
              <li key={payment.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900">
                    {money(payment.amount)} · {t(`subscription.page.method.${payment.method}`)}
                  </div>
                  <div className="text-gray-500">
                    {t("subscription.page.period", {
                      from: formatDate(payment.period_start),
                      to: formatDate(payment.period_end),
                    })}
                  </div>
                  <div className="text-gray-400 text-xs" dir="ltr">
                    {payment.document_number}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => viewDocument(payment.id)}
                  className="shrink-0 text-primary-700 font-medium hover:underline"
                >
                  {t(payment.document_type === "invoice" ? "subscription.page.invoice" : "subscription.page.receipt")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Subscription;
